// AcceptingPlayersToggleCard — agent's own locator opt-out toggle.
//
// History: was originally on /me/dashboard (Phase R4). Phase 6 moved
// it temporarily into a Dashboard accordion when /me was consolidated
// into /. Phase 6.3 (2026-05-11) is its final home — directly above
// the Shareable Agent ID on the Players sub-tab (/people/players).
// The reasoning: it controls who gets added to your player roster,
// so it belongs next to the player list, not on the metrics page.
//
// Visual: this is the ORIGINAL design from the old My Pool page
// (border-2, p-4, solid pill background, full descriptive copy,
// uppercase header). NOT the trimmed version that was in the
// Dashboard accordion.
//
// Behaviour:
//   - Default = ON (accepting). Flipping fires
//     PATCH /admin/me-agent/opt-out which writes an audit row
//     (`agent.opt_out_change`, source='self') and updates the
//     agents.is_accepting_players column. The locator's L1-L5
//     cascade reads that column, so the agent disappears from
//     organic-signup attribution within the next refresh of the
//     candidate set.
//   - Optimistic UI: flips the visual state immediately and rolls
//     back on error so the agent gets instant feedback.

import { useEffect, useState } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useT } from '@baaligames/shared-ui/lib/i18n';

export default function AcceptingPlayersToggleCard() {
  const { admin, refresh } = useAdminAuth();
  const t = useT();
  const me = admin as any;
  // MySQL TINYINT(1) → number; coerce to boolean for the toggle.
  const initialAccepting = !!me?.is_accepting_players;
  const [accepting, setAccepting] = useState<boolean>(initialAccepting);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reseed when admin context refreshes (e.g. after an admin
  // override forced a flip from elsewhere).
  useEffect(() => {
    setAccepting(!!me?.is_accepting_players);
  }, [me?.is_accepting_players]);

  // Only render for agents — admins don't have a locator opt-out.
  if (admin?.role !== 'agent') return null;

  async function flip() {
    if (busy) return;
    const newVal = !accepting;
    setAccepting(newVal);          // optimistic
    setBusy(true); setErr(null);
    try {
      await adminApi.patch('/admin/me-agent/opt-out', { accepting: newVal });
      await refresh();             // re-hydrate auth state
    } catch (e) {
      setAccepting(!newVal);       // rollback
      setErr((e as ApiError).message || t('agent.people.acceptingToggle.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`rounded-xl border-2 p-4 ${accepting
      ? 'border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/20'
      : 'border-amber-400 dark:border-amber-500/50 bg-amber-50 dark:bg-amber-950/25'}`}>
      <div className="flex items-start gap-3">
        <span className={`grid place-items-center w-9 h-9 rounded-xl shrink-0 ${accepting
          ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'
          : 'bg-amber-500/20 text-amber-700 dark:text-amber-300'}`}>
          <Icon name={accepting ? 'task_alt' : 'pause_circle'} size={20} filled />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-extrabold uppercase tracking-wider mb-0.5
                          text-arena-700 dark:text-neon-200/85">
            {t('agent.people.acceptingToggle.label')}
          </div>
          <div className={`text-[13px] leading-relaxed ${accepting
            ? 'text-emerald-900 dark:text-emerald-100'
            : 'text-amber-900 dark:text-amber-100'}`}>
            {/* Two separate keys per state — the ACCEPTING vs OPTED OUT
                emphasis must be preserved across translations. Each
                sentence carries its own bold treatment baked into the
                key string so translators can move the strong markers
                around in their language's word order. */}
            {t(accepting
                ? 'agent.people.acceptingToggle.bodyAccepting'
                : 'agent.people.acceptingToggle.bodyOptedOut')}
          </div>
          {err && <p className="text-[11.5px] text-rose-600 dark:text-rose-300 mt-1.5">{err}</p>}
        </div>
        {/* Pill switch — single tap, instant flip with optimistic UI. */}
        <button
          type="button"
          onClick={flip}
          disabled={busy}
          aria-pressed={accepting}
          aria-label={t(accepting ? 'agent.people.acceptingToggle.aria.stop' : 'agent.people.acceptingToggle.aria.start')}
          className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full
                      transition disabled:opacity-50 ${accepting
                        ? 'bg-emerald-600'
                        : 'bg-amber-500'}`}>
          <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
            accepting ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>
    </div>
  );
}
